<template>
  <v-container>
    <v-card :elevation="10">
      <v-card-title>
        <h3>整合性確認表の保存</h3>
      </v-card-title>
      <v-card-text
        class="text-center pa-10">
        <v-alert
          v-if="!saved"
          dark
          outlined
          color="info">
          この内容を保存します。よろしいですか？
        </v-alert>
        <v-alert
          v-else
          dark
          outlined
          color="success">
          <v-icon
            color="success">
            mdi-check
          </v-icon>
          整合性確認表を保存しました。
        </v-alert>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title top-contents-title">
            委託業務名
          </v-col>
          <v-col
            cols="7"
            class="text-left title top-contents-text">
            {{ step1Form.title }}
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            施設名
          </v-col>
          <v-col
            cols="7"
            class="text-left title border-class contents-text">
            {{ step1Form.facility_name }}
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            所在地
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            {{ step1Form.address }}
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            分類
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            <span v-if="step1Form.category === 1">
              建築物
            </span>
            <span v-else>
              工作物
            </span>
          </v-col>
        </v-row>
        <v-row
          v-if="step1Form.category === 2"
          dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            設置場所
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            <span v-if="step1Form.placement === 1">
              屋内
            </span>
            <span v-else>
              屋外
            </span>
          </v-col>
        </v-row>
        <v-row
          v-if="step1Form.category === 1"
          dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            建物情報
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            階数:{{ step1Form.floors }}階 延床面積:{{ step1Form.floor_space }}m2
          </v-col>
        </v-row>
        <v-row
          v-if="step1Form.category === 1"
          dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            建物構造
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            <span v-if="structures && step1Form.structures">
              {{ structuresText }}
            </span>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            建物用途
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            <span v-if="features && step1Form.features">
              {{ featuresText }}
            </span>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            調査の種類
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            {{ surveysType }}
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            書面調査期間
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            {{ formatDate(step2Form.written_survey_started_at) }}
            〜
            {{ formatDate(step2Form.written_survey_finished_at) }}
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            現地調査期間
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            {{ formatDate(step2Form.field_survey_started_at) }}
            〜
            {{ formatDate(step2Form.field_survey_finished_at) }}
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            実施者
          </v-col>
          <v-col
            cols="7"
            class="text-left title contents-text">
            <div
              v-for="(user, index) in rolesUserInformation"
              :key="index"
              class="border-underline">
              {{ rolesName(user.pivot.role_id) }}
              {{ user.name }}
              <span v-if="user.pivot.main_license_code">
                [{{ user.pivot.main_license_code }}]
              </span>
              <template v-if="rolesInformation(user)">
                (
                <span>
                  {{ userAdditionalInformation(user.pivot) }}
                </span>
                )
              </template>
            </div>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col
            cols="5"
            class="text-center font-weight-bold title contents-title">
            部屋情報
          </v-col>
          <v-col
            v-if="step3Form.rooms"
            cols="7"
            class="text-left title contents-text ">
            <span :class="{ 'error--text': step3Form.rooms.length===0 }">
              計{{ step3Form.rooms.length }}部屋
            </span>
            <div
              v-for="(room, index) in step3Form.rooms"
              :key="index"
              class="border-underline">
              [{{ room.room_no }}]
              {{ room.room_name }}
              {{ `(${floorsText(room.floors)})` }}
            </div>
          </v-col>
        </v-row>
        <v-row
          justify="center"
          class="mt-10"
          dense>
          <v-checkbox
            v-if="!isApprovalUser"
            v-model="isCheckedSendMail"
            color="success">
            <template #label>
              <div class="success--text font-weight-bold">承認申請を出す(メールで通知する)</div>
            </template>
          </v-checkbox>
        </v-row>
        <v-row v-if="!saved && isApprovalUser">
          <v-col>
            <v-btn
              v-if="!saved"
              dark
              x-large
              class="mb-10 mr-5"
              color="primary"
              :disabled="loading"
              @click.prevent="onSaveButtonClicked(false)">
              保存する
            </v-btn>
            <v-btn
              dark
              x-large
              class="mb-10"
              color="success"
              :disabled="loading"
              @click.prevent="onSaveButtonClicked(true)">
              承認する
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col>
            <v-btn
              v-if="!saved"
              dark
              x-large
              class="mb-10 ml-5"
              color="primary"
              :disabled="loading"
              @click.prevent="onSaveButtonClicked(false)">
              保存する
            </v-btn>
            <v-btn
              v-else
              dark
              x-large
              class="mb-10"
              color="primary"
              @click.prevent="onNextClicked">
              <v-icon>mdi-exit-run</v-icon>
              トップに戻る
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <global-footer>
      <template #left>
        <v-btn
          v-if="!saved"
          large
          @click.prevent="onReturnClicked">
          <v-icon>mdi-chevron-left</v-icon>
          戻る
        </v-btn>
      </template>
      <template #right>
        <v-btn
          v-if="saved"
          large
          color="warning"
          @click.prevent="onNextClicked">
          修了
          <v-icon right>mdi-exit-run</v-icon>
        </v-btn>
      </template>
    </global-footer>
  </v-container>
</template>

<script>
import formatter from '../../logic/format'
import downloadFile from '../../logic/download'
import {
  getStepSession, markAsSavedSession, setStepSession,
} from '../../logic/session'

export default {
  name: 'ConfirmationFormStep4',
  components: { },
  mixins: { formatter },
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
    structures: {
      type: Array,
      default: () => ([]),
    },
    features: {
      type: Array,
      default: () => ([]),
    },
    roles: {
      type: Array,
      default: () => ([]),
    },
    licenseTypes: {
      type: Array,
      default: () => ([]),
    },
  },
  data() {
    return {
      loading: false,
      saved: false,
      step1Form: {},
      step2Form: {},
      step3Form: {},
      project: {},
      isCheckedSendMail: false,
    }
  },
  computed: {
    userAdditionalInformation() {
      return (userPivot) => {
        const licenseNames = this.licenseTypes.map((license) => license.name)
        const assignedPlace = userPivot.assigned_place ? `担当箇所:${userPivot.assigned_place}` : ''
        const otherLicenseType = userPivot.other_license_type ? `その他の修了証:${licenseNames[userPivot.other_license_type - 1]}` : ''
        const otherLicenseCode = userPivot.other_license_code ? `修了証番号:${userPivot.other_license_code}` : ''
        const tel = userPivot.telephone ? `連絡先:${userPivot.telephone}` : ''
        const rolesUserInformation = [assignedPlace, otherLicenseType, otherLicenseCode, tel]
        return rolesUserInformation.filter(v => v).join('、')
      }
    },
    rolesInformation() {
      return (user) => user.pivot.assigned_place
        || user.pivot.other_license_type
        || user.pivot.telephone
        || false
    },
    structuresText() {
      if (this.structures.length === 0 || !this.step1Form.structures) return ''
      const structureItems = this.step1Form.structures.map((structureId) => {
        const structureItem = this.structures.filter(structure => structure.id === structureId)[0]
        const structuresOtherText = this.step1Form.structures_other_text ? `(${this.step1Form.structures_other_text})` : ''
        return structureItem.has_other
          ? `${structureItem.name}${structuresOtherText}`
          : structureItem.name
      })
      return structureItems.reverse().join('、')
    },
    featuresText() {
      if (this.features.length === 0 || !this.step1Form.features) return ''
      const featureItems = this.step1Form.features.map((featureId) => {
        const featureItem = this.features.filter(feature => feature.id === featureId)[0]
        const featuresOtherText = this.step1Form.features_other_text ? `(${this.step1Form.features_other_text})` : ''
        return featureItem.has_other
          ? `${featureItem.name}${featuresOtherText}`
          : featureItem.name
      })
      return featureItems.join('、')
    },
    surveysType() {
      return this.step2Form.type === 1 ? '石綿障害規則第3条に基づく事前調査' : 'その他の事前調査'
    },
    rolesUserInformation() {
      return this.step2Form.users
    },
    rolesName() {
      return (roleId) => this.roles[roleId]?.name
    },
    floorsText() {
      return (value) => (value > 0 ? `${value}F` : `B${Math.abs(value)}`)
    },
    isApprovalUser() {
      if (this.$store.state.auth.permission === 2) {
        return true
      }
      return this.project.users
        ? this.project.users.filter(
          user => (
            user.id === this.$store.state.auth.id
            && user.pivot.role_id === 2
            && user.has_approval_authority
          ),
        ).length > 0
        : false
    },
  },
  mounted() {
    this.loadItem()
  },
  methods: {
    loadItem() {
      this.step1Form = getStepSession('confirmation', 1)
      this.step2Form = getStepSession('confirmation', 2)
      this.step3Form = getStepSession('confirmation', 3)
      this.saved = !!getStepSession('confirmation', 4)
      this.fetchItem()
    },
    async saveItem(approved) {
      this.loading = true
      const form = {
        ...this.step1Form,
        ...this.step2Form,
        ...this.step3Form,
        isCheckedSendMail: this.isCheckedSendMail,
      }
      if (approved) {
        form.confirmation_approved = 1
      }
      try {
        const { data } = this.isNew
          ? await window.axios.post('/api/confirmations', form)
          : await window.axios.put(`/api/confirmations/${this.id}`, form)
        markAsSavedSession('confirmation')
        setStepSession('confirmation', 4, `${data.id}`)
        this.$store.commit(
          'setSystemMessage',
          { message: '情報を変更しました。', color: 'success' },
        )
      } catch ({ response }) {
        this.saved = false
      }

      this.loading = false
    },
    async fetchItem() {
      if (!this.isNew) {
        const { data } = await window.axios.get(`/api/confirmations/${this.id}`)
        this.project = data
      }
    },
    onSaveButtonClicked(approved = false) {
      this.saveItem(approved)

      this.saved = true
      this.$emit('hasChange', false)
    },
    onReturnClicked() {
      this.$emit('change', 3)
    },
    onNextClicked() {
      this.$router.push({ name: 'index' })
    },
    onDownloadButtonClicked() {
      const id = getStepSession('confirmation', 4)
      const url = `/api/confirmations/${id}/download`
      downloadFile(url)
    },
  },
}
</script>

<style scoped>
.top-contents-title {
  background-color: #eeeeee;
  border: 1px solid black;
}
.top-contents-text {
  border: 1px solid black;
  border-left: none;
}
.contents-title {
  background-color: #eeeeee;
  border: 1px solid black;
  border-top: none;
}
.contents-text {
  border: 1px solid black;
  border-top: none;
  border-left: none;
}
.border-underline {
  border-bottom: 1px solid black;
}
</style>
